import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@cotera/client/v0/components/ai-elements/conversation';
import { ChatMessageIdProvider } from '@cotera/client/v0/components/ai-elements/chat-message-context';
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useEffect,
  type FC,
  type ReactNode,
} from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { CompactionMessage } from './compaction-message';
import { ChatInput } from './input';
import { HijackerQueueModel } from './hijacker-queue.model';
import { useInScopeModel } from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import {
  RenderMessage,
  CrossMessageToolGroup,
  buildMessageRenderPlan,
  type MessageActionsConfig,
} from './message';
import { ArtifactInlineDedupeProvider } from './artifact-inline-dedupe-context';
import {
  CitationProvider,
  type CitationClickHandler,
} from './citations/citation-context';
import { MermaidRepairProvider } from './mermaid/mermaid-repair-context';
import type { CustomToolRenderer } from './tool-renderers';
import type { PromptInputMessage } from '@cotera/client/v0/components/ai-elements/prompt-input';
import { ChatModel } from './chat.model';
import { MessageClass } from '@cotera/sdk/core';
import { useScrollToFirstUnread } from './use-scroll-to-first-unread';
import {
  useHijackerPlaceHolder,
  useMessageFooterItems,
} from './message-footers';
import { useParticipantNames } from './use-participant-names';
import { cn } from '@cotera/client/v0/lib/utils';
import { CHAT_COLUMN_MAX_WIDTH, CHAT_RAIL_GUTTER } from './chat-layout';

export interface PresentationalChatRef {
  setInputValue: (value: string) => void;
  setInputEditorState: (value: { type: 'doc'; content?: unknown[] }) => void;
  appendInputValue: (value: string) => void;
}

interface PresentationalChatProps {
  messages: MessageClass[];
  onSubmit: (message: PromptInputMessage) => void | Promise<void>;
  onStop?: (message?: PromptInputMessage) => void | Promise<void>;
  /**
   * When provided, submitting while a run is in flight enqueues the message
   * instead of cancelling the run (see {@link ChatInput}).
   */
  onQueue?: (message: PromptInputMessage) => void;
  inputFooter?: ReactNode;
  status: 'ready' | 'streaming' | 'submitted' | 'error' | 'loading';
  customSubmitButton?: ReactNode;
  readonly?: boolean;
  /** Minimal chatModel for RenderMessage compatibility */
  chatModel: Pick<
    ChatModel,
    | 'lastMessageId'
    | 'status'
    | 'addToolResult'
    | 'id'
    | 'model'
    | 'inputWatchable'
  >;
  /** Content to render above the input box */
  inputHeader?: ReactNode;
  /**
   * Content rendered above the composer and above {@link inputHeader} (e.g. the
   * queued-messages list), so the header's status tab stays flush against the
   * composer. Unlike `inputHeader` it is not part of the seed header collapse
   * animation.
   */
  aboveComposer?: ReactNode;
  /** Content to render below the input box */
  inputBelow?: ReactNode;
  /** When true, do not render the input (caller renders it separately) */
  hideInput?: boolean;
  /** Config for which message actions to show (default: all) */
  messageActionsConfig?: MessageActionsConfig;
  /** When provided, mention chips (in messages and input) are clickable */
  onMentionClick?: MessageActionsConfig['onMentionClick'];
  /** When provided, inline citation chips route their clicks here */
  onCitationClick?: CitationClickHandler;
  /** When set, scroll to this message on initial load */
  firstUnreadMessageId?: string | null;
  /** When true, uses TipTap rich text editor instead of plain textarea */
  useRichText?: boolean;
  /** When true, accepts drops anywhere on document */
  globalDrop?: boolean;
  /**
   * Optional custom tool renderers. Either:
   * - Record of exact tool key -> component
   * - Function for pattern-based matching (e.g. coco/agent-<uuid>)
   * Takes precedence over v0 registry.
   */
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null);
  conversationId?: string;
  currentUserId?: string;
  /** Default placeholder text for the input field */
  inputPlaceholder?: string;
  /**
   * When true, suppresses the input hijacker UI. Useful for nested sub-agents
   * where hijackers should be rendered in the parent chat's input instead.
   */
  suppressHijackers?: boolean;
  /**
   * When true, uses a plain overflow scroll region instead of StickToBottom.
   * Needed when this chat is nested inside another scrollable (e.g. main coco
   * chat): nested StickToBottom + wheel listeners fight trackpad scrolling.
   */
  useNativeConversationScroll?: boolean;
  /**
   * Message footer handler ids to omit (see `registerMessageFooter`).
   * Sub-agent embeds use this to hide the global "thinking" dots footer.
   */
  excludedMessageFooterIds?: readonly string[];
  /** Vertically center the input chrome when there are no visible messages (e.g. chat seed). */
  centerInputWhenEmpty?: boolean;
}

const MessageChainFooters: FC<{
  conversationId: string;
  excludedMessageFooterIds?: readonly string[];
}> = ({ conversationId, excludedMessageFooterIds }) => {
  const items = useMessageFooterItems(conversationId);
  const visibleItems = useMemo(() => {
    if (excludedMessageFooterIds === undefined) {
      return items;
    }
    if (excludedMessageFooterIds.length === 0) {
      return items;
    }
    const omit = new Set(excludedMessageFooterIds);
    return items.filter((item) => !omit.has(item.id));
  }, [items, excludedMessageFooterIds]);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <>
      {visibleItems.map((item) => (
        <div key={item.id} className="mt-4">
          {item.content}
        </div>
      ))}
    </>
  );
};

export const PresentationalChat = forwardRef<
  PresentationalChatRef,
  PresentationalChatProps
>((props, ref) => {
  const {
    messages,
    onSubmit,
    onStop,
    onQueue,
    status,
    inputFooter,
    customSubmitButton,
    readonly = false,
    chatModel,
    inputHeader,
    aboveComposer,
    inputBelow,
    hideInput = false,
    messageActionsConfig,
    onMentionClick,
    onCitationClick,
    firstUnreadMessageId,
    useRichText = false,
    globalDrop,
    customToolRenderers,
    conversationId,
    currentUserId,
    inputPlaceholder = 'What would you like to know?',
    suppressHijackers = false,
    useNativeConversationScroll = false,
    excludedMessageFooterIds,
    centerInputWhenEmpty = false,
  } = props;

  const inputRef = useRef<{
    setValue: (v: string | ((prev: string) => string)) => void;
    setEditorState: (v: { type: 'doc'; content?: unknown[] }) => void;
    setPlaceholder: (placeholder: string) => void;
  }>(null);

  // Prevent input from jumping when header collapses: reserve height and animate to 0
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const headerHeightRef = useRef(0);
  const [collapsingHeight, setCollapsingHeight] = useState(0);
  const hasInputHeader = inputHeader !== null && inputHeader !== undefined;
  const prevHasInputHeaderRef = useRef(hasInputHeader);
  const collapseRaf1Ref = useRef<number | null>(null);
  const collapseRaf2Ref = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!hasInputHeader) {
      return;
    }
    const el = headerWrapperRef.current;
    if (el === null) {
      return;
    }
    headerHeightRef.current = el.offsetHeight;
  }, [hasInputHeader, inputHeader]);

  useEffect(() => {
    const prevHasInputHeader = prevHasInputHeaderRef.current;
    prevHasInputHeaderRef.current = hasInputHeader;

    const cancelPendingCollapseFrames = () => {
      if (collapseRaf1Ref.current !== null) {
        cancelAnimationFrame(collapseRaf1Ref.current);
        collapseRaf1Ref.current = null;
      }
      if (collapseRaf2Ref.current !== null) {
        cancelAnimationFrame(collapseRaf2Ref.current);
        collapseRaf2Ref.current = null;
      }
    };

    if (hasInputHeader) {
      cancelPendingCollapseFrames();
      setCollapsingHeight(0);
      return;
    }

    if (!prevHasInputHeader) {
      cancelPendingCollapseFrames();
      return;
    }

    const h = headerHeightRef.current;
    if (h <= 0) {
      return;
    }
    cancelPendingCollapseFrames();
    setCollapsingHeight(h);
    collapseRaf1Ref.current = requestAnimationFrame(() => {
      collapseRaf1Ref.current = null;
      collapseRaf2Ref.current = requestAnimationFrame(() => {
        collapseRaf2Ref.current = null;
        setCollapsingHeight(0);
      });
    });
    return () => {
      cancelPendingCollapseFrames();
    };
  }, [hasInputHeader]);

  useImperativeHandle(ref, () => ({
    setInputValue: (value: string) => {
      inputRef.current?.setValue(value);
    },
    setPlaceholder: (placeholder: string) => {
      inputRef.current?.setPlaceholder(placeholder);
    },
    setInputEditorState: (value: { type: 'doc'; content?: unknown[] }) => {
      inputRef.current?.setEditorState(value);
    },
    appendInputValue: (value: string) => {
      inputRef.current?.setValue((prev: string) =>
        prev ? `${prev} ${value}` : value
      );
    },
  }));

  // Create a minimal chatModel-like object for input compatibility
  const hasIncompleteToolCalls = messages.some((msg) => {
    if (!msg.isAssistantMessage()) {
      return false;
    }
    return msg.parts.some((part) => {
      if (!part.isToolPart()) {
        return false;
      }
      return part.isIncomplete();
    });
  });

  const contentRef = useScrollToFirstUnread(firstUnreadMessageId);

  // Use HijackerQueueModel from ModelScopeFactory
  const hijackerQueueModel = useInScopeModel(HijackerQueueModel);
  Assert.assert(
    hijackerQueueModel !== null,
    'HijackerQueueModel should be provided by ModelScopeFactory'
  );

  // Subscribe to full queue state from model
  const hijackerQueue = useWatchableValue(
    hijackerQueueModel.queueStateWatchable
  );

  const hijackerPlaceHolder = useHijackerPlaceHolder(hijackerQueue);

  const inputChatModel = useMemo(
    () =>
      ({
        id: chatModel.id,
        model: chatModel.model,
        addToolResult: (toolCallId: string, result: unknown) => {
          chatModel.addToolResult(toolCallId, result);
        },
        inputWatchable: chatModel.inputWatchable,
        // Use prop `status` (reactive in v1 Coco) — not `chatModel.status`: the memo
        // previously omitted status from deps so cancel → ready never reached ChatInput.
        status,
        hasIncompleteToolCalls: () => hasIncompleteToolCalls,
        sendMessage: async (message: PromptInputMessage) => {
          await onSubmit(message);
        },
      } as ChatModel),
    [chatModel, hasIncompleteToolCalls, onSubmit, status]
  );

  const participantNameById = useParticipantNames({
    conversationId,
    currentUserId,
    messages,
  });

  // Determine placeholder: active footer can override
  // Tool approval footer uses id 'tool-approval'
  const activePlaceholder = hijackerPlaceHolder?.content ?? inputPlaceholder;

  useEffect(() => {
    inputRef.current?.setPlaceholder(activePlaceholder);
  }, [activePlaceholder, inputRef]);

  /**
   * Lets a failed Mermaid diagram ask Coco to rewrite itself (see
   * {@link MermaidRepairProvider}). The repair is an ordinary user turn, so it
   * is only offered when the conversation is idle — passing a handler while a
   * response is streaming would interleave two turns, and the error card hides
   * the button rather than showing a dead one when this is `undefined`.
   */
  const onRepairDiagram = useMemo(
    () =>
      status === 'ready'
        ? (prompt: string) => {
            void onSubmit({ type: 'text', text: prompt });
          }
        : undefined,
    [status, onSubmit]
  );

  const hasVisibleMessages = messages.some((message) => !message.isHidden());
  const shouldCenterInput =
    centerInputWhenEmpty === true && !hasVisibleMessages;

  const messagesSection = (
    <ArtifactInlineDedupeProvider messages={messages}>
      <CitationProvider messages={messages} onCitationClick={onCitationClick}>
        <MermaidRepairProvider onRepair={onRepairDiagram}>
          <div ref={contentRef}>
            {buildMessageRenderPlan(messages, customToolRenderers).map(
              (entry) => {
                // A run of consecutive single-tool messages (e.g. sequential
                // edit-agent steps) collapses into one grouped row.
                if (entry.kind === 'toolRun') {
                  const anchorId = entry.messages[0]!.id;
                  return (
                    <div
                      key={`toolrun-${anchorId}`}
                      data-message-id={anchorId ?? undefined}
                    >
                      <ChatMessageIdProvider value={anchorId ?? null}>
                        <CrossMessageToolGroup
                          messages={entry.messages}
                          chatModel={chatModel}
                          readonly={readonly}
                          customToolRenderers={customToolRenderers}
                        />
                      </ChatMessageIdProvider>
                    </div>
                  );
                }

                const message = entry.message;
                if (message.isHidden()) {
                  return null;
                }

                // Use type guard to check if this is a compaction message
                if (message.isCompactionMessage()) {
                  return (
                    <div key={message.id}>
                      <CompactionMessage message={message} />
                    </div>
                  );
                }

                return (
                  <div
                    key={message.id}
                    data-message-id={message.id ?? undefined}
                  >
                    <ChatMessageIdProvider value={message.id ?? null}>
                      <RenderMessage
                        chatModel={chatModel}
                        message={message}
                        currentUserId={currentUserId}
                        participantNameById={participantNameById}
                        readonly={readonly}
                        messageActionsConfig={{
                          ...messageActionsConfig,
                          onMentionClick:
                            onMentionClick ??
                            messageActionsConfig?.onMentionClick,
                        }}
                        customToolRenderers={customToolRenderers}
                      />
                    </ChatMessageIdProvider>
                  </div>
                );
              }
            )}
            {/* Render registered footer items (e.g., "Waiting for approval") */}
            {conversationId !== undefined && (
              <MessageChainFooters
                conversationId={conversationId!}
                excludedMessageFooterIds={excludedMessageFooterIds}
              />
            )}
          </div>
        </MermaidRepairProvider>
      </CitationProvider>
    </ArtifactInlineDedupeProvider>
  );

  return (
    <div
      className={cn(
        'flex w-full min-h-0 flex-col',
        useNativeConversationScroll ? null : 'h-full',
        // Seed/empty state centers the input. Own the overflow *inside* this pane
        // (scroll internally when the seed chrome exceeds the viewport) rather than
        // growing past the fixed-height ancestors. Delegating overflow to the shared
        // page scroll container let native scroll-anchoring shove the chat offscreen
        // on the first send. See `min-h-full` centering wrapper below.
        shouldCenterInput && 'overflow-y-auto [overflow-anchor:none]'
      )}
    >
      {!shouldCenterInput &&
        (useNativeConversationScroll ? (
          // Natural document height: scroll is owned by an ancestor (e.g. sub-agent
          // max-height wrapper). Nested StickToBottom breaks trackpad scroll there.
          <div className={CHAT_RAIL_GUTTER} role="log">
            <div
              className={cn(CHAT_COLUMN_MAX_WIDTH, 'mx-auto w-full p-4 md:p-8')}
            >
              {messagesSection}
            </div>
          </div>
        ) : (
          <Conversation>
            <ConversationContent
              scrollClassName={CHAT_RAIL_GUTTER}
              className={CHAT_COLUMN_MAX_WIDTH}
            >
              {messagesSection}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>
        ))}
      {!readonly && !hideInput && !suppressHijackers && (
        <div
          className={cn(
            CHAT_RAIL_GUTTER,
            shouldCenterInput && 'flex min-h-full flex-col justify-center pb-8'
          )}
        >
          {/* Queued messages sit above the status header so the header's tab
              stays flush against the composer's top edge. */}
          {aboveComposer !== null && aboveComposer !== undefined ? (
            <div
              className={cn(
                CHAT_COLUMN_MAX_WIDTH,
                'mx-auto w-full px-4 md:px-8 [&:empty]:hidden'
              )}
            >
              {aboveComposer}
            </div>
          ) : null}
          <div
            className={cn(
              CHAT_COLUMN_MAX_WIDTH,
              'mx-auto w-full px-4 md:px-8 [&:empty]:hidden'
            )}
          >
            {hasInputHeader ? (
              <div ref={headerWrapperRef}>{inputHeader}</div>
            ) : collapsingHeight > 0 ? (
              <div
                style={{
                  height: collapsingHeight,
                  overflow: 'hidden',
                  transition: 'height 0.2s ease-out',
                }}
              />
            ) : null}
          </div>
          <div
            className={cn(
              CHAT_COLUMN_MAX_WIDTH,
              'mx-auto w-full px-4 md:px-8',
              // `md:pb-3` matches the artifact card's `md:py-3` in
              // `ArtifactSidePanel`, so the composer and the card bottom out on
              // the same line when they sit side by side. They are siblings in
              // one `ResizablePanelGroup` of equal height, so equal bottom
              // padding is all the alignment takes — but it does mean these two
              // values are a pair, and changing one alone will visibly stagger
              // the panes.
              //
              // Mobile keeps `pb-6`: there is no card beside it there, so this
              // is just the composer's own breathing room.
              shouldCenterInput ? 'pb-0' : 'pb-6 md:pb-3'
            )}
          >
            <ChatInput
              ref={inputRef}
              chatModel={inputChatModel}
              inputFooter={inputFooter}
              customSubmitButton={customSubmitButton}
              onSubmit={undefined}
              onStop={onStop}
              onQueue={onQueue}
              useRichText={useRichText}
              globalDrop={globalDrop}
              onMentionClick={onMentionClick}
              placeholder={activePlaceholder}
            />
          </div>
          {inputBelow !== null && inputBelow !== undefined ? (
            <div
              className={cn(
                CHAT_COLUMN_MAX_WIDTH,
                'mx-auto w-full px-4 md:px-8 [&:empty]:hidden'
              )}
            >
              {inputBelow}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});

PresentationalChat.displayName = 'PresentationalChat';
